/**
 * Dependency-free helpers extracted from general-utils.
 *
 * The login bundle needs these, but general-utils pulls in moment-timezone, sanitize-html and
 * numeral at module scope. Keep this file free of heavy imports so pre-login code stays small.
 */
import cryptoRandomString from 'crypto-random-string';

/**
 * @method errorFromResponse
 * @description Builds a translated error message from a failed API response.
 * @param {Function} t Translation function
 * @return {Function} (action, error, defaultMessage) => string
 */
export const errorFromResponse = (t) => (action, error, defaultMessage) => {
  /* To notify user that it was an auth error instead generic message */
  if (error && error.response && error.response.status === 401) {
    return `${t('common.label.error')}: ${t(action)}: ${t('common.auth.api_not_auth')}`;
  }

  let message = error?.response?.data?.message || defaultMessage;

  // regex to find labels for translation. Example: "$$phone_number$$"
  const regex = /\$\$([^$]+)\$\$/g;
  let match;
  while ((match = regex.exec(message)) !== null) {
    message = message.replace(match[0], t(match[0]));
  }
  return message
    ? `${t('common.label.error')}: ${t(action)}: ${t(message)}`
    : t('common.label.standard_error', { action: t(action) });
};

const _processKeyValue = (key, value) => {
  if (key.startsWith('$$') && key.endsWith('$$')) {
    return value.replace(/[@${}|]/g, (match) => `{'${match}'}`) || null;
  }
  return value.replace(/[@$]/g, (match) => `{'${match}'}`) || null;
};

/**
 * @method flattenedTranslations
 * @description Flattens a nested translations object into dot-separated keys.
 * @param {object} data Nested translations
 * @return {object} Flattened translations
 */
export const flattenedTranslations = (data) => {
  let result = {};
  const getProperty = (currentValue, prop) => {
    for (let key in currentValue) {
      if (currentValue[key]) {
        let newKey = key;
        let newVal = currentValue[key];
        if (prop.length > 0) {
          newKey = prop + '.' + key;
        }
        if (typeof newVal === 'object') {
          getProperty(newVal, newKey);
        } else {
          result[newKey] = _processKeyValue(newKey, newVal);
        }
      }
    }
  };
  getProperty(data, '');
  return result;
};

/**
 * @method removeNestedBrackets
 * @description Strips content wrapped in double square brackets.
 * @param {string} value Value to clean
 * @return {string} Value without the bracketed sections
 */
export const removeNestedBrackets = (value) => {
  let stack = [];
  let output = '';
  let index = 0;
  while (index < value?.length) {
    let char = value[index];
    if (char === '[' && value[index + 1] === '[') {
      stack.push('[[');
      index++;
    } else if (stack.length > 0 && char === ']' && value[index + 1] === ']') {
      stack.pop();
      index++;
    } else if (stack.length === 0) {
      output += char;
    }
    index++;
  }
  return output;
};

/**
 * @method generateRequestId
 * @description Generates a random identifier used to correlate requests.
 * @param {number} length Identifier length
 * @return {string} Generated identifier
 */
export const generateRequestId = (length = 15) => cryptoRandomString({ length });
